import React, { useEffect } from 'react';
import { Transition } from 'react-transition-group';
import classnames from 'classnames';

import { Menu } from 'components/navigation';
import { Image, Link, Scrollbar } from 'components/ui';
import { CONFIG } from 'constants/config';
import { useAnalytics } from 'hooks/useAnalytics';

import type { IMegaMenuBanner, IMenuItemLink } from 'types';

import styles from './MegaMenuContent.module.scss';

// Types

interface Props {
  className?: string,
  //
  selectedItem?: IMenuItemLink,
  //
  style?: React.CSSProperties,
  //
  onMouseEnter?: () => void,
  onMouseLeave?: () => void
}

interface MegaMenuBannerProps {
  banner?: IMegaMenuBanner,
  categoryIndex: number,
  categorySlug: string
}

const MegaMenuContent = (props: Props) => {
  const {
    className,
    //
    selectedItem,
    //
    style,
    //
    onMouseEnter,
    onMouseLeave,
  } = props;

  const {
    children: items = [], banner, categoryIndex, slug: categorySlug
  } = selectedItem || {};

  const isValidCategory = (typeof categoryIndex === 'number') && !!categorySlug;

  // Refs

  const transitionRef = React.useRef<HTMLDivElement>(null);

  // Hooks

  const analytics = useAnalytics();

  // Render

  return (

    <Transition
      nodeRef={transitionRef}
      in={!!items?.length}
      timeout={400}
    >
      {
        (state) => {
          return (
            <div
              ref={transitionRef}
              className={classnames(
                styles.root,
                { [styles[state]]: state },
                className,
              )}
              style={style}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              <Scrollbar
                className={styles.scrollbar}
                autoHide={false}
                forceVisible="y"
              >
                <div
                  className={classnames(
                    styles.inner,
                    { [styles[state]]: state }
                  )}
                >

                  {/* Menu */}
                  <Menu
                    className={styles.menu}
                    listClassName={styles.list}
                    itemClassName={styles.item}
                    linkClassName={styles.link}
                    subLinkClassName={styles.subLink}
                    items={items}
                    onItemClick={(item) => {
                      if (item?.label) {
                        analytics.selectCategory(item?.label);
                      }
                    }}
                  />

                  {/* Banner Image */}
                  {
                    isValidCategory
                    && (
                      <MegaMenuBanner
                        banner={banner}
                        categoryIndex={categoryIndex}
                        categorySlug={categorySlug}
                      />
                    )
                  }
                </div>
              </Scrollbar>
            </div>
          );
        }
      }
    </Transition>
  );
};

const MegaMenuBanner = (props: MegaMenuBannerProps) => {

  const { banner, categoryIndex, categorySlug } = props;
  const { image, url } = banner || {};

  // Hooks

  const analytics = useAnalytics();

  useEffect(() => {
    // Tracking every view of promotion banners in the mega menu
    if (image) {
      analytics.viewMegaMenuBanner(categoryIndex, parseBannerName(categorySlug, image));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [image]);

  // Handlers

  const handleClick = React.useCallback(() => {
    if (image) {
      analytics.selectMegaMenuBanner(categoryIndex, parseBannerName(categorySlug, image));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIndex, categorySlug, image]);

  // Render

  return (
    <div className={styles.imageContainer}>
      {
        banner && (
          <Link
            href={url || '#'}
            prefetch={CONFIG.PREFETCH_LINKS}
            passHref
          >
            <div>
              {image
              && (
                <Image
                  className={styles.image}
                  src={image}
                  width={275}
                  height={485}
                  onClick={handleClick}
                />
              )}
            </div>
          </Link>
        )
      }
    </div>
  );
};

// Helpers

const parseBannerName = (categorySlug: string, url: string) => {
  const filename = url?.split('/').pop();
  return `${categorySlug}+${filename}`;
};

// Export

export default MegaMenuContent;
